import { motion } from 'framer-motion';

// Shared container style for consistency
const VisualContainer = ({ children }: { children: React.ReactNode }) => (
    <div className="w-full aspect-[4/3] bg-gradient-to-br from-white to-blue-50/50 rounded-xl border border-blue-100 shadow-sm overflow-hidden relative flex items-center justify-center p-3 sm:p-6 md:p-8">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#3b82f60a_1px,transparent_1px),linear-gradient(to_bottom,#3b82f60a_1px,transparent_1px)] bg-[size:16px_16px] [mask-image:radial-gradient(ellipse_at_center,black_60%,transparent_100%)] pointer-events-none" />
        {children}
    </div>
);

// Component 1: Logical Slide Flow
export const SlideFlowVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 p-3 sm:p-6 flex flex-col items-center justify-center relative overflow-hidden z-10">
                {/* Flowchart Sequence */}
                <div className="flex gap-1 md:gap-3 items-center mb-8">
                    {[
                        { title: "Problem", color: "bg-red-50 text-red-600 border-red-100" },
                        { title: "Solution", color: "bg-blue-50 text-blue-600 border-blue-100" },
                        { title: "Market", color: "bg-green-50 text-green-600 border-green-100" }
                    ].map((slide, i) => (
                        <div key={i} className="flex items-center gap-1 md:gap-3">
                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                whileInView={{ scale: 1, opacity: 1 }}
                                transition={{ delay: i * 0.4, type: "spring", stiffness: 200 }}
                                className={`w-16 h-12 md:w-20 md:h-14 rounded-lg border-2 ${slide.color} flex items-center justify-center shadow-sm relative bg-white`}
                            >
                                <span className="text-[9px] font-bold uppercase tracking-wider">{slide.title}</span>
                                {/* Slide decorators */}
                                <div className="absolute top-1.5 left-1.5 w-6 h-0.5 bg-gray-100 rounded-full" />
                                <div className="absolute bottom-1.5 right-1.5 w-2 h-2 rounded-full bg-gray-50" />
                            </motion.div>

                            {/* Connecting Arrow (except for last item) */}
                            {i < 2 && (
                                <motion.div
                                    initial={{ width: 0, opacity: 0 }}
                                    whileInView={{ width: 12, opacity: 1 }}
                                    transition={{ delay: i * 0.4 + 0.2, duration: 0.3 }}
                                    className="h-[2px] bg-gray-300 rounded-full relative"
                                >
                                    <div className="absolute -right-1 -top-1 w-2 h-2 border-t-[2px] border-r-[2px] border-gray-300 rotate-45" />
                                </motion.div>
                            )}
                        </div>
                    ))}
                </div>

                {/* AI Suggestion Badge */}
                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.5, type: "spring" }}
                    className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-blue-200 shadow-md text-blue-600"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span className="text-xs font-bold">Optimal Narrative Flow Generated</span>
                </motion.div>

                {/* Background Decor */}
                <svg className="absolute inset-0 pointer-events-none opacity-20" aria-hidden="true">
                    <path d="M 50 50 Q 150 10 250 50" stroke="#e5e7eb" fill="none" strokeDasharray="4 4" />
                    <path d="M 50 250 Q 150 290 250 250" stroke="#e5e7eb" fill="none" strokeDasharray="4 4" />
                </svg>

            </div>
        </VisualContainer>
    );
};

// Component 2: Content Refinement
export const ContentRefinementVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 p-3 sm:p-6 relative overflow-hidden">
                <div className="flex flex-col h-full space-y-2 sm:space-y-4">
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-gray-50 pb-3 shrink-0">
                        <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Smart Refine</span>
                        </div>
                        <div className="px-2 py-0.5 bg-green-50 text-green-700 text-[10px] font-bold rounded-full border border-green-100 flex items-center gap-1">
                            <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                            Optimized
                        </div>
                    </div>

                    {/* Content Transformation */}
                    <div className="relative flex-1">
                        {/* Old Content (Fading Out) */}
                        <motion.div
                            initial={{ opacity: 1, y: 0 }}
                            whileInView={{ opacity: 0, y: -10 }}
                            transition={{ delay: 2, duration: 0.5 }}
                            className="absolute inset-0"
                        >
                            <h4 className="text-sm font-semibold text-gray-400 mb-2">Q4 Results Summary</h4>
                            <ul className="space-y-3">
                                <li className="flex items-start gap-2 text-xs text-gray-400 leading-relaxed">
                                    <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-300 shrink-0" />
                                    We made good money this quarter.
                                </li>
                                <li className="flex items-start gap-2 text-xs text-gray-400 leading-relaxed">
                                    <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-300 shrink-0" />
                                    Users seem to like the new features.
                                </li>
                            </ul>
                        </motion.div>

                        {/* New Content (Fading In) */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: 2.2, duration: 0.5 }}
                            className="absolute inset-0"
                        >
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">Q4 Performance Overview</h4>
                            <ul className="space-y-4">
                                <li className="flex items-start gap-2 text-xs text-gray-600 leading-relaxed">
                                    <div className="mt-0.5 w-4 h-4 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                                        <svg className="w-2.5 h-2.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <span>
                                        <strong className="text-gray-900">Revenue exceeded projections</strong> by 15%, driven by enterprise adoption.
                                    </span>
                                </li>
                                <li className="flex items-start gap-2 text-xs text-gray-600 leading-relaxed">
                                    <div className="mt-0.5 w-4 h-4 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                                        <svg className="w-2.5 h-2.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <span>
                                        <strong className="text-gray-900">User engagement spiked 40%</strong> following the release of the new dashboard.
                                    </span>
                                </li>
                            </ul>
                        </motion.div>
                    </div>

                    {/* Magic Wand Animation */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.5, left: "50%", top: "50%" }}
                        whileInView={{
                            opacity: [0, 1, 1, 0],
                            scale: [0.5, 1.2, 1, 1.5],
                            left: ["50%", "80%", "20%", "50%"],
                            top: ["50%", "20%", "80%", "50%"]
                        }}
                        transition={{ duration: 2, times: [0, 0.2, 0.8, 1], ease: "easeInOut" }}
                        className="absolute w-24 h-24 bg-blue-100/30 blur-xl rounded-full pointer-events-none -translate-x-1/2 -translate-y-1/2 mix-blend-multiply"
                    />
                </div>
            </div>
        </VisualContainer>
    );
};

// Component 3: Instant Refinement (Layout Adaptation)
export const InstantRefinementVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] bg-white rounded-lg shadow-md border border-gray-100 p-6 relative overflow-hidden flex flex-col gap-4">

                {/* AI Prompt Bubble */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="self-end bg-blue-600 text-white px-3 py-2 rounded-lg rounded-tr-none text-xs font-medium shadow-sm flex items-center gap-2"
                >
                    <svg className="w-3 h-3 text-blue-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Turn this list into a timeline
                </motion.div>

                {/* Content Area */}
                <div className="border border-dashed border-gray-200 rounded-lg p-4 bg-gray-50/50 relative h-32 overflow-hidden">
                    {/* State 1: List View (Fades Out) */}
                    <motion.div
                        initial={{ opacity: 1, scale: 1 }}
                        whileInView={{ opacity: 0, scale: 0.9 }}
                        transition={{ delay: 2, duration: 0.5 }}
                        className="space-y-3 absolute inset-4"
                    >
                        <div className="w-3/4 h-3 bg-gray-200 rounded mb-2" />
                        <div className="space-y-2">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="flex gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1 shrink-0" />
                                    <div className="w-full h-2 bg-gray-100 rounded" />
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    {/* State 2: Timeline View (Fades In) */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 2.2, duration: 0.5 }}
                        className="absolute inset-4 flex items-center"
                    >
                        <div className="relative w-full">
                            {/* Line */}
                            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-blue-100 -translate-y-1/2" />

                            {/* Points */}
                            <div className="flex justify-between relative z-10">
                                {['2023', '2024', '2025'].map((year, i) => (
                                    <div key={i} className="flex flex-col items-center gap-2">
                                        <div className="w-2.5 h-2.5 bg-blue-500 rounded-full ring-2 ring-white" />
                                        <div className="text-[10px] font-bold text-gray-500">{year}</div>
                                        <div className="w-8 h-1 bg-gray-100 rounded" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </VisualContainer>
    );
};

// Component 4: Smart Control (Accept/Reject)
export const SmartControlVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] relative z-10 flex flex-col items-center">

                {/* Main Content Card (The ""Slide"") */}
                <div className="w-full bg-white rounded-lg shadow-lg border border-gray-100 p-1 relative overflow-hidden group">
                    {/* Proposed Change Highlighting */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ delay: 0.5, duration: 0.8 }}
                        className="absolute inset-0 bg-blue-500/5 z-20 pointer-events-none border-2 border-blue-500/20 rounded-lg"
                    />

                    <div className="bg-gray-50 rounded aspect-[16/9] flex items-center justify-center relative overflow-hidden">
                        <img
                            src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070&auto=format&fit=crop"
                            alt="Data visualization"
                            className="w-full h-full object-cover opacity-80"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <span className="bg-white/90 px-3 py-1 text-xs font-bold text-gray-800 rounded shadow-sm backdrop-blur-sm">
                                Proposed Image
                            </span>
                        </div>
                    </div>
                </div>

                {/* Control Actions */}
                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    whileInView={{ y: -10, opacity: 1 }}
                    transition={{ delay: 1, type: "spring" }}
                    className="flex gap-2 relative z-30"
                >
                    <button className="flex items-center gap-1.5 px-4 py-2 bg-white rounded-full text-red-600 text-xs font-bold shadow-lg border border-red-50 hover:bg-red-50 transition-colors">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Reject
                    </button>
                    <button className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 rounded-full text-white text-xs font-bold shadow-lg hover:bg-blue-700 transition-colors ring-2 ring-white">
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Apply Changes
                    </button>

                    {/* Cursor Simulation */}
                    <motion.div
                        initial={{ x: 50, y: 50, opacity: 0 }}
                        whileInView={{ x: 0, y: 0, opacity: 1 }}
                        transition={{ delay: 2, duration: 1 }}
                        className="absolute -right-6 -bottom-6"
                    >
                        <svg className="w-6 h-6 text-gray-900 drop-shadow-md" viewBox="0 0 24 24" fill="currentColor" stroke="white" strokeWidth="2">
                            <path d="M5.65376 12.3673H5.46026L5.31717 12.4976L0.500002 16.8829L0.500002 1.19169L11.7118 9.20839L8.14441 9.20839C7.79542 9.20839 7.50002 9.49379 7.50002 9.84278V12.0007L5.65376 12.3673Z" />
                        </svg>
                    </motion.div>
                </motion.div>

            </div>
        </VisualContainer>
    );
};
